/**
 * Social-feed scatter-gather merge (issue #7).
 *
 * Firestore caps `in` queries at 30 values. A chain with >30 tracked Twitter handles
 * therefore cannot be served by a single `where('handleId','in',handles)` query —
 * the old code did `handles.slice(0, 30)`, silently dropping every long-tail handle.
 *
 * The fix is standard scatter-gather pagination: split the handles into chunks of 30,
 * run one ordered+limited query per chunk in parallel, then MERGE the chunk results
 * here. Because each `handleId` belongs to exactly ONE chunk, the chunk result sets are
 * disjoint — there are no cross-chunk duplicates, so the merge is a clean
 * flatten → sort → slice.
 *
 * This module is the PURE merge math, deliberately dependency-free (no Firestore types)
 * so it is node-testable in isolation.
 */

/**
 * Minimal shape this merge needs from a tweet. The real `Tweet` type satisfies it:
 *  - `createdAt` is a Firestore Timestamp (has `.toMillis()` / `.seconds`) when sorting by recency
 *  - `trendingScore` is a number when sorting by engagement
 * We read those defensively (see `orderValue`) so callers can pass real Tweets directly.
 */
export type MergeableTweet = {
  createdAt?: unknown;
  trendingScore?: unknown;
};

export type OrderField = 'createdAt' | 'trendingScore';

export interface MergeChunkedResult<T> {
  /** Globally-sorted (desc by orderField), sliced to pageSize. */
  tweets: T[];
  /**
   * Value cursor for the NEXT page: the orderField value of the LAST tweet in the
   * sliced result. Pass this back as the `startAfter` boundary for every chunk query
   * on load-more. `null` when the page is empty.
   *
   * We use a VALUE cursor (not a Firestore docSnapshot) on purpose: a docSnapshot
   * cursor is only valid for the one query/chunk it came from, so it cannot drive a
   * scatter-gather load-more across N chunks. A value cursor works for all chunks.
   */
  boundaryValue: number | string | null;
  /** Whether another page may exist (see `mergeChunkedTweets` for the rule). */
  hasMore: boolean;
}

/**
 * Extracts the orderField value as a comparable scalar.
 *
 * - `trendingScore` is already a number.
 * - `createdAt` is a Firestore Timestamp; we normalize it to epoch-ms so it can be
 *   compared numerically and round-tripped as a `startAfter` boundary. We tolerate the
 *   several shapes a timestamp can take (Timestamp instance, `{seconds}`, Date, ms number).
 *
 * Returns a number when possible; missing/unparseable values become `-Infinity` so they
 * sort LAST in a descending sort (and never bubble to the top of the feed).
 */
export function orderValue(tweet: MergeableTweet, orderField: OrderField): number {
  const raw = tweet?.[orderField];

  if (raw == null) return -Infinity;

  if (orderField === 'trendingScore') {
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : -Infinity;
  }

  // createdAt → epoch ms, tolerating Timestamp / {seconds} / Date / number
  const anyRaw = raw as any;
  if (typeof anyRaw === 'number' && Number.isFinite(anyRaw)) return anyRaw;
  if (typeof anyRaw?.toMillis === 'function') {
    const ms = anyRaw.toMillis();
    return typeof ms === 'number' && Number.isFinite(ms) ? ms : -Infinity;
  }
  if (typeof anyRaw?.toDate === 'function') {
    const d = anyRaw.toDate();
    const ms = d instanceof Date ? d.getTime() : NaN;
    return Number.isFinite(ms) ? ms : -Infinity;
  }
  if (anyRaw instanceof Date) {
    const ms = anyRaw.getTime();
    return Number.isFinite(ms) ? ms : -Infinity;
  }
  if (typeof anyRaw?.seconds === 'number') {
    return anyRaw.seconds * 1000 + (typeof anyRaw.nanoseconds === 'number' ? anyRaw.nanoseconds / 1e6 : 0);
  }

  return -Infinity;
}

/**
 * Merges the per-chunk result sets of a scatter-gather social-feed fetch into one page.
 *
 * Steps:
 *  1. Flatten all chunks (disjoint by handleId → no dedupe needed).
 *  2. Stable sort DESC by `orderField` (ties keep input order — see caveat below).
 *  3. Slice to `pageSize`.
 *  4. `boundaryValue` = orderField value of the last sliced tweet (or null if empty).
 *  5. `hasMore` = true if the flattened set had MORE than pageSize items, OR if any
 *     single chunk came back full (a full chunk page implies that chunk may have more
 *     docs past its own limit, even if the merged-and-sliced page wasn't itself full).
 *
 * @param chunks       per-chunk arrays of tweets (already ordered+limited server-side)
 * @param orderField   'createdAt' (recency) or 'trendingScore' (engagement)
 * @param pageSize     how many tweets one page should contain
 * @param opts.anyChunkFull
 *        Optional explicit signal that at least one chunk query returned a full
 *        `limit(pageSize)` page. If omitted, `hasMore` is derived purely from
 *        `flattened.length > pageSize`. Pass this when you know a chunk maxed out its
 *        limit, so load-more isn't prematurely disabled.
 *
 * CAVEAT — ties on equal orderField value: when two tweets share the exact same
 * orderField value AND that value equals the `startAfter` boundary, Firestore's
 * `startAfter(value)` is EXCLUSIVE on that value, so a tweet sharing the boundary value
 * could be skipped on the next page. This is an inherent limitation of value cursors
 * (vs docSnapshot cursors) and is vanishingly rare for ms-precision `createdAt`; it is a
 * little more plausible for integer `trendingScore`. The cost is at most a few boundary
 * tweets occasionally omitted from a long-tail feed — strictly better than the previous
 * behavior of dropping ALL handles past the first 30.
 */
export function mergeChunkedTweets<T extends MergeableTweet>(
  chunks: T[][],
  orderField: OrderField,
  pageSize: number,
  opts: { anyChunkFull?: boolean } = {}
): MergeChunkedResult<T> {
  const safeChunks = Array.isArray(chunks) ? chunks.filter(Array.isArray) : [];
  const flattened: T[] = ([] as T[]).concat(...safeChunks);

  if (flattened.length === 0) {
    return { tweets: [], boundaryValue: null, hasMore: false };
  }

  // Stable desc sort: decorate-sort-undecorate preserves input order on ties.
  const decorated = flattened.map((tweet, index) => ({
    tweet,
    value: orderValue(tweet, orderField),
    index,
  }));
  decorated.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value; // desc
    return a.index - b.index; // stable on ties
  });

  const size = pageSize > 0 ? pageSize : 0;
  const sliced = decorated.slice(0, size).map((d) => d.tweet);

  const last = sliced[sliced.length - 1];
  const boundaryValue =
    last !== undefined ? rawBoundary(last, orderField) : null;

  const hasMore = flattened.length > size || opts.anyChunkFull === true;

  return { tweets: sliced, boundaryValue, hasMore };
}

/**
 * The boundary value to feed back into `startAfter` for the next page.
 *
 * For `trendingScore` we return the numeric score directly. For `createdAt` we return
 * the normalized epoch-ms (from `orderValue`) — callers translate that back into a
 * Firestore-comparable cursor. We never return the raw Timestamp object here so the
 * boundary stays a plain serializable scalar (number | string | null).
 */
function rawBoundary(tweet: MergeableTweet, orderField: OrderField): number | string | null {
  const v = orderValue(tweet, orderField);
  return Number.isFinite(v) ? v : null;
}
